import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TestIntroScreen } from "@/components/public/test/TestIntroScreen";
import { TestExpiredScreen } from "@/components/public/test/TestExpiredScreen";
import { TestAlreadySubmittedScreen } from "@/components/public/test/TestAlreadySubmittedScreen";
import { TestThankYouScreen } from "@/components/public/test/TestThankYouScreen";
import { TestQuestionsForm } from "@/components/public/test/TestQuestionsForm";
import type { TestQuestion } from "@/types/test";

export const Route = createFileRoute("/test/$token")({
  component: PublicTestPage,
});

interface ActivePayload {
  status: "active";
  clientFirstName: string;
  snapshotName: string;
  snapshotQuestions: TestQuestion[];
  expiresAt: string;
}
type Payload = ActivePayload | { status: "expired" } | { status: "submitted" };

type Phase = "intro" | "filling" | "submitted" | "expired" | "already";

function PublicTestPage() {
  const { token } = Route.useParams();
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["public", "test", token],
    queryFn: async () => {
      const res = await fetch(`/api/test-assignments/by-token/${token}`);
      if (res.status === 404) throw new Error("not_found");
      if (!res.ok) throw new Error("fetch");
      return res.json() as Promise<Payload>;
    },
    retry: false,
  });

  if (isLoading) return <p className="p-6 text-center text-muted-foreground">{t("common.loading")}</p>;
  if (error) return <p className="p-6 text-center text-muted-foreground">{t("tests.public.notFound")}</p>;
  if (!data) return null;

  if (phase === "submitted") return <TestThankYouScreen />;
  if (phase === "expired" || data.status === "expired") return <TestExpiredScreen />;
  if (phase === "already" || data.status === "submitted") return <TestAlreadySubmittedScreen />;

  // At this point data.status must be "active" — the other two statuses returned above
  const active = data as ActivePayload;

  if (phase === null) {
    return (
      <TestIntroScreen
        firstName={active.clientFirstName}
        testName={active.snapshotName}
        expiresAt={active.expiresAt}
        onStart={() => setPhase("filling")}
      />
    );
  }

  return (
    <TestQuestionsForm
      token={token}
      questions={active.snapshotQuestions}
      onSubmitted={() => setPhase("submitted")}
      onExpired={() => setPhase("expired")}
      onAlreadySubmitted={() => setPhase("already")}
    />
  );
}
